// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { Header } from './components/Header';
import { PrivateRoute } from './components/PrivateRoute';
import { useAuth0, Auth0Provider } from '@auth0/auth0-react';
import { UserControls } from './components/UserControls';
import { BrokerConnections } from './components/Connections/BrokerConnections';
import { Charts } from "./components/Charts";
import { Admin } from './components/Admin';
import { AlgoMetrics } from './components/AlgoMetrics';
import { OptionsPerformance } from './components/OptionsPerformance';
import { ErrorBoundary } from './components/ErrorBoundary';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/theme.css';
import { Footer } from './components/Footer';
import { hasTenant } from './utils/Permissions';
import { TENANT_INVESTCEO } from './utils/constants';
import { TermsPage, PrivacyPage } from './components/Legal';
import { determineTenant } from './utils/tenants';

function App() {
  const { loading } = useAuth0();

  useEffect(() => {
    const tenant = determineTenant();
    const branding = {
      smithfield: {
        title: 'Smithfield Trading',
        icon: `${process.env.PUBLIC_URL}/smithfield/favicon.png`,
        description: 'Smithfield Trading — automated futures trading platform for Smithfield members.',
      },
      investceo: {
        title: 'InvestCEO',
        icon: `${process.env.PUBLIC_URL}/investceo/favicon.png`,
        description: 'InvestCEO — automated futures trading platform for InvestCEO members.',
      },
    };
    const config = branding[tenant];
    if (config) {
      document.title = config.title;
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = config.icon;
      const description = document.querySelector("meta[name='description']");
      if (description) {
        description.content = config.description;
      }
    }
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  const getCallbackURL = () => {
    if (window.location.pathname.includes('/trade/')) {
      return `${window.location.origin}/connect`;
    } else {
      return `${window.location.origin}${window.location.pathname}`;
    }
  }

  return (
    <ErrorBoundary>
      <Auth0Provider
        domain={process.env.REACT_APP_IAM_DOMAIN}
        clientId={process.env.REACT_APP_IAM_CLIENT_ID}
        authorizationParams={{
          redirect_uri: getCallbackURL(),
          audience: process.env.REACT_APP_IAM_AUDIENCE
        }}
        cacheLocation={process.env.NODE_ENV === 'development' ? 'localstorage' : 'memory'}
      >
        <BrowserRouter>
          <div className="App flex flex-col min-h-screen">
            <Header />
            <main className="flex-1">
              <Routes>
                <Route path="/legal/terms" element={<TermsPage />} />
                <Route path="/legal/privacy" element={<PrivacyPage />} />
                <Route path="*" element={
                  <PrivateRoute>
                    <Routes>
                      <Route path="/connect" element={<BrokerConnections />} />
                      <Route path="/trade" element={<UserControls />} />
                      <Route path="/trade/*" element={<UserControls />} />
                      {/* Same caveat as /options-performance below: hasTenant() reads a
                          module-level cache filled asynchronously, and App never re-renders
                          when it lands. This currently works only because hasTenant falls
                          back to investceo for an empty tenant list, so it happens to be
                          true pre-load — gate inside Charts if that fallback ever changes. */}
                      <Route path="/performance" element={hasTenant(TENANT_INVESTCEO) ? <Charts /> : <Navigate to="/connect" replace />} />
                      {/* Gate lives inside the component: hasTenant() reads a module-level
                          cache populated asynchronously, and App never re-renders when it
                          lands — evaluating it here would redirect before permissions load. */}
                      <Route path="/options-performance" element={<OptionsPerformance />} />
                      <Route path="/metrics" element={<AlgoMetrics />} />
                      <Route path="/admin" element={<Admin />} />
                      <Route path='*' element={<Navigate to='/connect' />} />
                    </Routes>
                  </PrivateRoute>
                } />
              </Routes>
            </main>
            <Footer />
          </div>
        </BrowserRouter>
      </Auth0Provider>
    </ErrorBoundary>
  );
}

export default App;